<template>
  <div>
    <!--    Enhanced Fee Payment Form-->
    <validation-observer
      ref="feePaymentForm"
      v-slot="{invalid}"
    >
      <lenon-modal
        title="Record Payments"
        :show="feePaymentModalOpened"
        :show-overlay="loadingPendingFees"
        size="md"
        @onClose="closeFeeSettingModal()"
      >
        <error-display :error="error" />
        
        <!-- Student Search Section -->
        <div class="mb-3">
          <label class="form-label">
            Search Students
            <b class="text-danger" style="font-size: 15px">*</b>
          </label>
          <div class="row">
            <div class="col-md-12">
              <lenon-input 
                v-model="searchTerm" 
                name="search_term"
                placeholder="Search students by name or ID"
                suffix-icon="SearchIcon"  
                @onEnterPressed="performSearch" 
                @onSuffixIconClick="performSearch" 
              />
            </div>
          </div>
        </div>

        <!-- Search Results -->
        <div v-if="searchResults.length > 0" class="mb-3">
          <label class="form-label">
            <feather-icon icon="SearchIcon" size="16" class="mr-1" />
            Search Results ({{ searchResults.length }})
          </label>
          <div style="max-height: 350px; overflow-y: auto;">
            <b-list-group>
              <student-card
                v-for="result in searchResults"
                :key="result.student_id"
                :student="result"
                :is-selected="selectedStudent && selectedStudent.student_id === result.student_id"
                @select="selectStudent"
              />
            </b-list-group>
          </div>
        </div>

        <!-- Selected Student Display -->
        <div v-if="selectedStudent" class="mb-3">
          <div class="mt-2 d-flex justify-content-center">
            <lenon-profile-photo
              :photo="selectedStudent.photo"
              size="100"
            />
          </div>
          <div class="text-center mt-2">
            <h5>{{ fullName }}</h5>
          </div>
        </div>

        <div
          v-if="madeFullPayment"
          class="text-center"
        >
          <b-badge variant="light-success">
            <feather-icon
              icon="CheckCircleIcon"
              class="mr-25"
            />
            <span>Student has made full payment for selected fee</span>
          </b-badge>
        </div>

        <lenon-select
          v-model="feePayment.fee_setting_id"
          label-name="display_name"
          value-name="id"
          label="Fee Type"
          placeholder="Select Fee Type"
          :options="feeSettings"
          rules="required"
        />

        <div
          v-if="pendingFee"
          class="mt-2 mb-2"
        >
          <div class="row">
            <div class="col-4">
              Total: <strong>{{ pendingFee.total_amount }}</strong>
            </div>
            <div class="col-4">
              Paid: <strong>{{ pendingFee.paid_amount }}</strong>
            </div>
            <div class="col-4">
              Pending: <strong>{{ pendingFee.pending_amount }}</strong>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              Due: <strong>{{ pendingFee.due_date }}</strong>
            </div>
          </div>
        </div>
        <lenon-input
          v-model="feePayment.amount"
          name="amount"
          placeholder="Enter Amount"
          :rules="`required|numeric|max_value:${pendingAmount}|min_value:1`"
          label="Amount"
        />
        <lenon-date-picker
          v-model="feePayment.date"
          name="date"
          placeholder="Select Date"
          label="Select Date"
          rules="required|no_future_date"
          :enable-time="true"
        />
        <lenon-input
          v-model="feePayment.payer_name"
          name="payer_name"
          placeholder="Enter Payer Name"
          label="Payer Name"
        />
        <lenon-input
          v-model="feePayment.payer_phone"
          name="payer_phone"
          placeholder="Enter Payer Phone"
          label="Payer Phone"
        />
        <b-row
          v-if="hasReceipt"
          class="float-right"
        >
          <div class="col-12">
            <lenon-button
              label="Generate Receipt for Last Payment"
              icon="PrinterIcon"
              variant="outline-primary"
              :loading="generatingReceipts"
              @onClick="printReceipt()"
            />
          </div>
        </b-row>
        <template slot="modal-actions">
          <b-row class="float-right">
            <lenon-button
              variant="flat-danger"
              icon="XIcon"
              class="mr-1"
              label="Cancel"
              @onClick="closeFeeSettingModal()"
            />
            <lenon-button
              label="Record Payment"
              :disabled="invalid || !selectedStudent"
              :loading="feePaymentLoading"
              loading-text="Loading..."
              @onClick="recordFeePayment()"
            />
          </b-row>
        </template>
      </lenon-modal>
    </validation-observer>
  </div>
</template>

<script>

import { BRow, BBadge, BAvatar, BListGroup } from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import LenonModal from '@/lenon/components/LenonModal.vue'
import LenonInput from '@/lenon/components/LenonInput.vue'
import LenonButton from '@/lenon/components/LenonButton.vue'
import showToast from '@/lenon/mixins/showToast'
import {
  RECORD_FEE_PAYMENT_M,
} from '@/graphql/mutations'
import LenonSelect from '@/lenon/components/LenonSelect.vue'
import { FETCH_STUDENTS_FOR_FEE_PAYMENTS_Q, LOAD_STUDENT_PENDING_FEE_Q } from '@/graphql/queries'
import LenonDatePicker from '@/lenon/components/LenonDatePicker.vue'
import logData from '@/libs/log'
import Receipt from '@/views/finance/fee-payments/Receipt.vue'
import eventBus from '@/lenon/utils/eventBus'
import receipt from '@/lenon/mixins/receipt'
import ErrorDisplay from '@/lenon/components/ErrorDisplay'
import LenonProfilePhoto from '@/lenon/components/LenonProfilePhoto'
import StudentCard from '@/views/finance/fee-discounts/StudentCard.vue'

export default {
  name: 'FeePaymentEnhanced',
  components: {
    StudentCard,
    LenonProfilePhoto,
    ErrorDisplay,
    Receipt,
    LenonDatePicker,
    LenonSelect,
    LenonButton,
    LenonInput,
    LenonModal,
    ValidationObserver,
    BRow,
    BBadge,
    BAvatar,
    BListGroup,
  },
  mixins: [showToast, receipt],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      error: {},
      searchTerm: '',
      loadingPendingFees: false,
      feePaymentModalOpened: false,
      feePaymentLoading: false,
      searching: false,
      selectedStudent: null,
      searchResults: [],
      feePayment: {
        fee_setting_id: null,
        amount: null,
        date: null,
        payer_name: null,
        payer_phone: null,
      },
      pendingFee: null,
    }
  },
  computed: {
    madeFullPayment() {
      return this.pendingFee?.pending_amount === 0
    },
    pendingAmount() {
      return this.pendingFee?.pending_amount || 0
    },
    fullName() {
      if (this.selectedStudent) {
        return `${this.selectedStudent.last_name} ${this.selectedStudent.first_name} ${this.selectedStudent.middle_name || ''}`
      }
      return ''
    },
    feeSettings() {
      const settings = this.$store.getters['feeSettings/feeSettings']
      if (this.selectedStudent) {
        return settings.filter(s => s.classes.includes(this.selectedStudent.class_id))
      }
      return settings
    },
    feeSettingId() {
      return this.feePayment.fee_setting_id
    },
    hasReceipt() {
      return !!this.selectedStudent?.receipt_id
    },
    studentClassId() {
      return this.selectedStudent?.class_id
    },
  },
  watch: {
    modalOpened(opened) {
      this.feePaymentModalOpened = opened
      if (opened) {
        this.resetForm()
      }
    },
    feeSettingId(id) {
      if (id && this.selectedStudent) {
        this.loadStudentPendingFees(id)
      } else {
        this.pendingFee = null
      }
    },
  },
  methods: {
    resetForm() {
      this.pendingFee = null
      this.selectedStudent = null
      this.searchResults = []
      this.searchTerm = ''
      this.feePayment = {
        fee_setting_id: null,
        amount: null,
        date: null,
        payer_name: null,
        payer_phone: null,
      }
      this.error = {}
      this.$refs.feePaymentForm.reset()
    },
    selectStudent(student) {
      this.selectedStudent = student
      this.searchResults = [] // Clear search results after selection
      this.searchTerm = '' // Clear search term
      
      if (this.feeSettingId) {
        this.loadStudentPendingFees(this.feeSettingId)
      }
    },
    performSearch() {
      if (!this.searchTerm.trim()) {
        this.showInfo('Please enter a search term')
        return
      }

      this.searching = true
      this.loadingPendingFees = true
      
      // Use the new REST API endpoint for student search
      this.$http.get('finance/students-for-fee-payment', {
        params: {
          search: this.searchTerm.trim()
        }
      })
        .then(res => {
          const students = res.data || []
          
          this.searchResults = students.map(student => ({
            ...student,
            type: 'student',
            id: student.student_id,
            student_id: student.reference,
            full_name: `${student.last_name} ${student.first_name} ${student.middle_name || ''}`,
            class_name: student.class_name || 'N/A'
          }))
          
          if (this.searchResults.length === 0) {
            this.showInfo('No students found matching your search')
          }
        })
        .catch(err => {
          logData(err)
          this.showError('Failed to search students')
          this.searchResults = []
        })
        .finally(() => {
          this.searching = false
          this.loadingPendingFees = false
        })
    },
    printReceipt() {
      this.generateReceipt({ 
        fee_ids: [this.selectedStudent.fee_id], 
        fee_setting_id: this.selectedStudent.fee_setting_id 
      })
    },
    loadStudentPendingFees(feeSettingId) {
      this.error = {}
      if (this.selectedStudent) {
        this.loadingPendingFees = true
        this.$apollo.query({
          query: LOAD_STUDENT_PENDING_FEE_Q,
          variables: { student_id: this.selectedStudent.id, fee_setting_id: feeSettingId },
        }).then(res => {
          logData(res)
          if (res.errors) {
            this.feePayment.fee_setting_id = null
            this.error = { graphQLErrors: res.errors }
          }
          this.loadingPendingFees = false
          this.pendingFee = res.data.studentPendingFee
        }).catch(err => {
          this.error = err
          logData(err)
          this.feePayment.fee_setting_id = null
          this.loadingPendingFees = false
          this.showError('Failed to load pending fee')
        })
      }
    },
    closeFeeSettingModal() {
      this.feePaymentModalOpened = false
      this.$emit('modalClosed')
    },
    recordFeePayment() {
      this.error = {}
      this.feePaymentLoading = true
      this.$apollo.mutate({
        mutation: RECORD_FEE_PAYMENT_M,
        variables: {
          input: {
            ...this.feePayment,
            student_id: this.selectedStudent.id,
            pending_amount: +this.pendingFee.pending_amount,
            amount: +this.feePayment.amount,
            payer_name: this.feePayment.payer_name || 'N/A',
            payer_phone: this.feePayment.payer_phone || 'N/A',
          },
        },
      })
        .then(res => {
          this.showSuccess('Recorded fee payment successfully')
          this.feePaymentLoading = false
          this.$store.commit('feePayments/updateStudentsForFeePayment', res.data.recordFeePayment)
          this.resetForm()
        })
        .catch(err => {
          this.error = err
          logData(err)
          this.showError('Failed to record fee payment')
          this.feePaymentLoading = false
        })
    },
  },
}
</script>

<style scoped>
/* Minimal custom styling for dark mode compatibility */
</style> 